import { Fragment, ReactNode } from 'react';
import { ModifierKey, ControlKey, AltKey } from 'v5/primitives/ModifierKey';

/**
 * Renders a keyboard shortcut string as React elements with proper platform-specific icons
 * @param shortcut - Raw shortcut string (e.g., "mod+alt+p")
 * @returns React elements representing the shortcut keys
 * @example
 * ```tsx
 * <KeyboardShortcut shortcut="mod+alt+p" />
 * // Renders: ⌘ + ⌥ + P (on Mac)
 * // Renders: Ctrl + Alt + P (on Windows)
 * ```
 */
export function KeyboardShortcut({ shortcut }: { shortcut: string }) {
  // Split by + and process each key
  const keys = shortcut.split('+').map(key => key.trim().toLowerCase());

  const getKeyLabel = (key: string): ReactNode => {
    switch (key) {
      case 'mod':
        return <ModifierKey />;
      case 'alt':
        return <AltKey />;
      case 'ctrl':
        return <ControlKey />;
      default:
        return <span className="capitalize">{key}</span>;
    }
  };

  return (
    <>
      {keys.map((key, index) => (
        <Fragment key={index}>
          {index > 0 && <span className="">+</span>}
          {getKeyLabel(key)}
        </Fragment>
      ))}
    </>
  );
}
